import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { Granularity, EngagementTimeseries, TSSeries } from './types';

const numberFmt = new Intl.NumberFormat('en-US');

// Green palette (dark → light); wraps if a listing has more engagement types
// than colours.
const SERIES_COLORS = ['#166534', '#84cc16', '#22c55e', '#4d7c0f', '#a3e635', '#065f46'];

/**
 * Only impressions and views get bars. A daily window is ~31 buckets, and four
 * grouped bars per bucket collapses into hairlines — the other types stay in the
 * legend and read out in full in the tooltip.
 */
const CHARTED_KEYS = new Set(['impression', 'impressions', 'view', 'views']);
/** Match the system name exactly — `preview` must not read as a `view`. */
const isCharted = (s: TSSeries) => CHARTED_KEYS.has((s.name_system ?? s.name ?? '').trim().toLowerCase());

const seriesTotal = (s: TSSeries) => (s.data ?? []).reduce((sum, p) => sum + (Number(p.count) || 0), 0);

const parseDay = (s: string) => new Date(`${s}T00:00:00`);
const advancePeriod = (d: Date, g: Granularity) => {
  const n = new Date(d);
  if (g === 'monthly') n.setMonth(n.getMonth() + 1);
  else if (g === 'weekly') n.setDate(n.getDate() + 7);
  else n.setDate(n.getDate() + 1);
  return n;
};
const periodKey = (d: Date, g: Granularity) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return g === 'monthly' ? `${y}-${m}` : `${y}-${m}-${day}`;
};
const periodLabel = (key: string, g: Granularity) => {
  const d = g === 'monthly' ? new Date(`${key}-01T00:00:00`) : new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  return g === 'monthly'
    ? d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const parsePeriod = (p: string): Date =>
  p.length === 7 ? new Date(`${p}-01T00:00:00`) : new Date(`${p}T00:00:00`);

/** Round the ms division so a DST shift inside the span can't cost a day. */
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86_400_000);
const addDays = (d: Date, n: number) => {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
};

/** "19–25 Jul" within a month, "26 Jul–1 Aug" across one. */
const spanLabel = (from: Date, toInclusive: Date) => {
  const sameMonth = from.getMonth() === toInclusive.getMonth() && from.getFullYear() === toInclusive.getFullYear();
  const start = from.toLocaleDateString(undefined, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' });
  const end = toInclusive.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return from.getTime() === toInclusive.getTime() ? end : `${start}–${end}`;
};

/** Per-bucket metadata the tooltip reads back off the hovered row. */
type ChartRow = Record<string, number | string | boolean>;

/** Lists every engagement type for the hovered period, bar or no bar. */
const EngagementTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ payload?: ChartRow }>;
  series: TSSeries[];
  colorAt: (index: number) => string;
}> = ({ active, label, payload, series, colorAt }) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  const partial = row.__partial === true;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
      <p className="text-xs font-medium text-gray-900">{(row.__range as string) || label}</p>
      {partial && (
        <p className="mb-1 text-[11px] text-amber-600">
          Partial period · {row.__days} of {row.__fullDays} days
        </p>
      )}
      {!partial && <div className="mb-1" />}
      <div className="space-y-0.5">
        {series.map((s, i) => (
          <div key={s.engtid ?? s.name} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-gray-600">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorAt(i) }} />
              {s.name}
            </span>
            <span className="tabular-nums font-medium text-gray-900">
              {numberFmt.format(Number(row[s.name]) || 0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Engagement-over-time chart for a listing — one grouped bar per engagement type
 * across the backend's `engagement_timeseries` window (daily by default). The
 * period axis is filled across the returned start/end range (union'd with any
 * periods present in the data) so gaps render as zero-height bars.
 */
export type EngagementGranularity = 'daily' | 'weekly' | 'monthly';

const VIEWS: EngagementGranularity[] = ['daily', 'weekly', 'monthly'];

export const EngagementTrendChart: React.FC<{
  loading: boolean;
  timeseries?: EngagementTimeseries;
  view: EngagementGranularity;
  onView: (view: EngagementGranularity) => void;
}> = ({ loading, timeseries, view, onView }) => {
  const backendGran: Granularity =
    timeseries?.granularity === 'monthly' ? 'monthly' : timeseries?.granularity === 'weekly' ? 'weekly' : 'daily';
  const series = timeseries?.series ?? [];

  // Daily payloads roll up client-side; a coarser payload renders as-is, since
  // buckets can't be disaggregated back into days.
  const gran: Granularity = backendGran === 'daily' ? view : backendGran;

  // Bins covering the window, each summing the raw points that fall inside it.
  const rows = useMemo(() => {
    const allDates: number[] = [];
    series.forEach((s) =>
      (s.data ?? []).forEach((p) => {
        const t = parsePeriod(p.period).getTime();
        if (!Number.isNaN(t)) allDates.push(t);
      }),
    );
    const start = timeseries?.start_date
      ? parseDay(timeseries.start_date)
      : allDates.length
        ? new Date(Math.min(...allDates))
        : null;
    const end = timeseries?.end_date
      ? parseDay(timeseries.end_date)
      : allDates.length
        ? new Date(Math.max(...allDates))
        : null;
    if (!start || !end) return [] as ChartRow[];

    let d = gran === 'monthly' ? new Date(start.getFullYear(), start.getMonth(), 1) : new Date(start);
    const bins: Array<{
      label: string;
      range: string;
      days: number;
      fullDays: number;
      partial: boolean;
      start: number;
      end: number;
    }> = [];
    let guard = 0;
    while (d <= end && guard < 800) {
      const binStart = new Date(d);
      const next = advancePeriod(binStart, gran);
      // A rolled-up bucket is labelled by the span it covers, clamped to the
      // window — the trailing one is usually short (a 31-day window leaves the
      // 5th weekly bucket holding 3 days) and must not read as a full week.
      const binEnd = addDays(next, -1);
      const covered = binEnd > end ? end : binEnd;
      const fullDays = daysBetween(binStart, binEnd) + 1;
      const days = daysBetween(binStart, covered) + 1;
      const stamp = periodLabel(periodKey(binStart, gran), gran);
      bins.push({
        label: gran === 'daily' ? stamp : spanLabel(binStart, covered),
        range: gran === 'daily' ? stamp : spanLabel(binStart, covered),
        days,
        fullDays,
        partial: gran !== 'daily' && days < fullDays,
        start: binStart.getTime(),
        end: next.getTime(),
      });
      d = next;
      guard++;
    }

    return bins.map((b) => {
      const row: ChartRow = {
        period: b.label,
        __range: b.range,
        __days: b.days,
        __fullDays: b.fullDays,
        __partial: b.partial,
      };
      series.forEach((s) => {
        let sum = 0;
        (s.data ?? []).forEach((p) => {
          const t = parsePeriod(p.period).getTime();
          if (t >= b.start && t < b.end) sum += Number(p.count) || 0;
        });
        row[s.name] = sum;
      });
      return row;
    });
  }, [timeseries, series, gran]);

  const colorAt = (index: number) => SERIES_COLORS[index % SERIES_COLORS.length];

  // Keep each series' palette index so the legend, bars and tooltip agree. If a
  // listing records neither impressions nor views, chart its two busiest types
  // rather than drawing nothing.
  const barSeries = useMemo(() => {
    const indexed = series.map((s, index) => ({ s, index }));
    const matched = indexed.filter(({ s }) => isCharted(s));
    if (matched.length) return matched;
    return [...indexed].sort((a, b) => seriesTotal(b.s) - seriesTotal(a.s)).slice(0, 2);
  }, [series]);

  const total = useMemo(
    () => series.reduce((sum, s) => sum + (s.data ?? []).reduce((a, b) => a + (Number(b.count) || 0), 0), 0),
    [series],
  );
  const hasData = total > 0;

  /** Set when the final bucket is short, e.g. a 31-day window's 5th week. */
  const partialTail = rows.length > 0 && rows[rows.length - 1].__partial === true ? rows[rows.length - 1] : null;

  // Thin the x-axis labels so a busy window doesn't overlap.
  const tickInterval = rows.length > 12 ? Math.ceil(rows.length / 8) : 0;

  const rangeLabel =
    timeseries?.start_date && timeseries?.end_date
      ? `${periodLabel(timeseries.start_date, 'daily')} – ${periodLabel(timeseries.end_date, 'daily')}`
      : null;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">Engagement over time</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {rangeLabel && <span className="hidden sm:inline">{rangeLabel}</span>}
          <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
            {VIEWS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => onView(g)}
                className={`px-2.5 py-1 text-xs font-medium capitalize ${
                  view === g ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="relative">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={1} barCategoryGap="12%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                <XAxis
                  dataKey="period"
                  tickLine={false}
                  axisLine={false}
                  interval={tickInterval}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                {/* Hidden Y axis — gridlines still render; exact values via tooltip. */}
                <YAxis hide allowDecimals={false} />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                  content={<EngagementTooltip series={series} colorAt={colorAt} />}
                />
                {barSeries.map(({ s, index }) => (
                  <Bar
                    key={s.engtid ?? s.name}
                    dataKey={s.name}
                    fill={colorAt(index)}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                    // A flat 2 draws a stub on every empty period; only real values earn one.
                    minPointSize={(value: number | null | undefined) => ((value ?? 0) > 0 ? 2 : 0)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          {series.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
              {series.map((s, i) => {
                const charted = barSeries.some((b) => b.index === i);
                return (
                  <span
                    key={s.engtid ?? s.name}
                    title={charted ? undefined : 'Shown in the tooltip only'}
                    className={`flex items-center gap-1.5 text-xs ${charted ? 'text-gray-600' : 'text-gray-400'}`}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={
                        charted
                          ? { backgroundColor: colorAt(i) }
                          : { boxShadow: `inset 0 0 0 1.5px ${colorAt(i)}` }
                      }
                    />
                    {s.name}
                  </span>
                );
              })}
            </div>
          )}

          {/* Axis ticks thin out on busy windows, so spell the short bucket out. */}
          {hasData && partialTail && (
            <p className="mt-2 text-[11px] text-gray-400">
              Each bar groups {gran === 'weekly' ? '7 days' : 'a month'}; the last covers{' '}
              {partialTail.__range as string} — {partialTail.__days} of {partialTail.__fullDays} days.
            </p>
          )}

          {/* Empty overlay — axis still shows so it never looks broken. */}
          {!hasData && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-gray-50 px-3 py-1 text-xs text-gray-400">
                No engagement recorded in this period
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
